import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import DataGrid, { Column, Summary, TotalItem, Paging, Pager } from 'devextreme-react/data-grid';
import { BorrowerApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import './borrowers.css';
import { formatCurrency, handleAdminError } from '../../common/SharedFunctions';
import { LinkType } from '../common/BorrowerConstants';
import { executeAction } from '../common/BorrowerFunctions';
import { ActionType } from '../common/constants/ActionType';
import { Tooltip } from 'devextreme-react/tooltip';
import { onActionsCellRender } from '../common/BorrowerFunctions';
import { LoanStatusType } from '@apply/constants';
import { useNavigate } from 'react-router-dom';
import { RefreshEvents } from '../../common/SharedEvents';
import { LoadPanel } from 'devextreme-react';

const BorrowersTable = ({ gridStore, loanStatusId = LoanStatusType.Outstanding, showDollars = false }) => {
    const searchState = useSelector((state) => state.searchState);
    const navigate = useNavigate();
    const [loadingMessage, set_loadingMessage] = useState('');
    const borrowerApi = new BorrowerApi.BorrowerApi(applicationInsights);
    const dataGrid = useRef(null);
    const allowedPageSizes = [5, 10, 15];
    const defaultPageSize = 5;

    const styles = {
        subordinateLinkageContainer: {
            margin: '10px',
            border: '1px solid lightgray',
            borderRadius: '10px 10px 0px 0px',
            backgroundColor: 'ghostwhite',
        },
        suborderinateLinkageContainerHeader: {
            fontWeight: 'bold',
            paddingLeft: '5px',
            width: '100%',
            display: 'flex',
        },
        subordinateLinkageContainerHeaderButton: {
            marginLeft: 'auto',
            paddingTop: '10px',
            paddingRight: '10px',
        },
        subordinateLinkageGridContainer: {
            marginLeft: '10px',
            marginRight: '10px',
            marginBottom: '10px',
            marginTop: '5px',
        },
    };

    const [dataStore, set_dataStore] = useState(null);
    const dataFunction = (criteria) => {
        try {
            set_loadingMessage('Loading...');
            criteria.loanStatusId = loanStatusId;
            return borrowerApi.getBorrowersGrid(criteria);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred! Please try refreshing the page.');
        } finally {
            set_loadingMessage('');
        }
    };

    const _afterRefresh = () => {
        const grid = dataGrid?.current?.instance;
        if (grid !== null && grid !== undefined) {
            grid.collapseAll(-1); //So that Master/Detail sectioon is not rendered funny if a row is deleted.
            grid.refresh();
        }
    };

    const setRef = (ref) => {
        if (ref) {
            dataGrid.current = ref;
        }
    };

    useEffect(() => {
        if (gridStore) {
        }

        const refreshEvents = new RefreshEvents();
        refreshEvents.addRefreshListener(_afterRefresh);

        return () => {
            refreshEvents.removeRefreshListener(_afterRefresh);
        };
    }, []);

    useEffect(() => {
        gridStore.setOnLoad('companyId', dataFunction, searchState?.searchText?.borrower);
        set_dataStore(gridStore.store);

        _afterRefresh();
    }, [loanStatusId, showDollars, searchState]);

    const showEntityModal = (entityId, companyId) => {
        var model = {
            actionId: ActionType.EditEntity,
            entityId,
            companyId,
            linkType: LinkType.Entity,
        };
        executeAction(model, false);
    };

    const showPrincipalModal = (principalId, entityId) => {
        var model = {
            actionId: ActionType.EditPrincipal,
            principalId,
            entityId,
            linkType: LinkType.Principal,
        };
        executeAction(model, false);
    };

    const linkPrincipalModal = (entityId) => {
        var model = {
            actionId: ActionType.EditBorrowerEntityToPrincipalLink,
            principalId: 0,
            entityId: entityId,
            linkType: LinkType.Principal,
        };
        executeAction(model, false);
    };

    const showBusinessModal = (companyId) => {
        if (!companyId) {
            return;
        }
        var model = {
            actionId: ActionType.EditCompany,
            companyId: companyId,
            linkType: LinkType.Company,
        };
        executeAction(model, navigate);
    };

    const onBusinessNameRender = (e) => {
        if (e.data.companyId === 0) {
            return <b style={{ color: 'darkred' }}>{e.value}</b>;
        } else {
            return (
                <button
                    className='link'
                    onClick={() => showBusinessModal(e.data.companyId)}
                >
                    {e.value}
                </button>
            );
        }
    };

    const onEntityNameRender = (e) => {
        if (e.data.entityId === 0) {
            return <b style={{ color: 'darkred' }}>{e.value}</b>;
        } else {
            return (
                <>
                    <button
                        className='link'
                        onClick={(ev) => showEntityModal(e.data.entityId, e.data.companyId)}
                    >
                        {e.value}
                    </button>
                    {e.data.entityId > 0 && (
                        <div style={{ paddingTop: '20px' }}>
                            <button
                                className='button hollow'
                                onClick={(ev) => showPrincipalModal(0, e.data.entityId)}
                                style={{ marginLeft: '5px' }}
                            >
                                <b>Add Principal</b>
                            </button>
                            <button
                                className='button hollow'
                                onClick={(ev) => linkPrincipalModal(e.data.entityId)}
                                style={{ marginLeft: '5px' }}
                            >
                                <b>Link Existing Principal</b>
                            </button>
                        </div>
                    )}
                </>
            );
        }
    };

    const onEntityPrincipalRender = (e) => {
        const targetId = `p_${e.data.principalId}`;
        const text =
            e.data.isOwner && e.data.isOfficer
                ? 'Officer/20%+ Owner'
                : e.data.isOwner
                  ? 'Owner'
                  : e.data.isOfficer
                    ? 'Officer'
                    : 'Other';
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    <button
                        className='link'
                        onClick={() => showPrincipalModal(e.data.principalId, e.data.entityId)}
                    >
                        {e.value}
                    </button>
                </div>
                <Tooltip
                    target={`[data-for="${targetId}"]`}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    {text}
                </Tooltip>
            </>
        );
    };

    const onBusinessLinkedPrincipalRender = (e) => {
        const targetId = `p_${e.data.principalId}`;
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    <button
                        className='link'
                        onClick={() => showPrincipalModal(e.data.principalId, e.data.entityId)}
                    >
                        {e.value}
                    </button>
                </div>
            </>
        );
    };

    const onHighlightCellRender = (e) => {
        if (!e.data.highlight) {
            return null;
        }
        const targetId = 'hrow' + e.data.id;
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    <span
                        className='reddot'
                        style={{ position: 'unset' }}
                    ></span>
                </div>
                <Tooltip
                    target={`[data-for="${targetId}"]`}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    {e.data.tooltip}
                </Tooltip>
            </>
        );
    };

    const onBorrowerPrincipalRender = (e) => {
        return (
            <>
                {e.data.principals && e.data.principals.length >= 1 && e.data.principals[0].principalId !== 0 && (
                    <DataGrid
                        id={'principalsGrid'}
                        keyExpr='principalId'
                        dataSource={e.data.principals}
                        applicationInsights={applicationInsights}
                        showColumnHeaders={false}
                        loadPanelEnabled={false}
                        showBorders={false}
                        showRowLines={false}
                        rowAlternationEnabled={false}
                        allowColumnResizing={false}
                        selectionMode='none'
                        remoteOperations={false}
                        repaintChangesOnly={true}
                    >
                        <Column
                            datafield='highlight'
                            cellRender={onHighlightCellRender}
                            width='20'
                        />
                        <Column
                            dataField='name'
                            cellRender={onEntityPrincipalRender}
                        />
                        <Column
                            type='actions'
                            alignment='center'
                            cellRender={(e) => onActionsCellRender(e, 'principalsGrid')}
                            width={50}
                            allowReordering={false}
                            allowSorting={false}
                            allowResizing={false}
                        />
                    </DataGrid>
                )}
            </>
        );
    };

    const onSubordinateLinkageRender = (e) => {
        if (!e.data.entities) {
            return null;
        }
        return (
            <>
                <div style={styles.subordinateLinkageContainer}>
                    <div style={styles.suborderinateLinkageContainerHeader}>
                        {e.data.companyId > 0 && (
                            <div style={styles.subordinateLinkageContainerHeaderButton}>
                                <button
                                    type='button'
                                    className='button'
                                    onClick={(ev) => showEntityModal(0, e.data.companyId)}
                                >
                                    Add Borrower Entity
                                </button>
                            </div>
                        )}
                    </div>
                    <div style={styles.subordinateLinkageGridContainer}>
                        <DataGrid
                            id={'entitiesGrid'}
                            keyExpr='entityId'
                            dataSource={e.data.entities}
                            applicationInsights={applicationInsights}
                            pagerVisible={e.data.entities.length > 25}
                            loadPanelEnabled={false}
                            showBorders={true}
                            showRowLines={true}
                            rowAlternationEnabled={true}
                            allowColumnResizing={true}
                            selectionMode='none'
                            remoteOperations={false}
                            allowedPageSizes={[5, 10, 15]}
                            defaultPageSize={5}
                            repaintChangesOnly={true}
                            width={'100%'}
                        >
                            <Column
                                dataField='name'
                                caption='Entity'
                                width={310}
                                cellRender={onEntityNameRender}
                                sortIndex={0}
                                sortOrder='asc'
                            />
                            <Column
                                type='actions'
                                alignment='center'
                                cellRender={(e) => onActionsCellRender(e, 'entitiesGrid')}
                                width={50}
                                allowReordering={false}
                                allowSorting={false}
                                allowResizing={false}
                            />
                            <Column
                                dataField='user'
                                cellRender={onBorrowerPrincipalRender}
                                caption='Principals'
                                allowSorting={false}
                                width={250}
                            />
                            <Column
                                dataField='loC_Count'
                                caption='LOC'
                                width={60}
                                allowResizing={false}
                                visible={!showDollars}
                                cellRender={(e) => (!e.value ? '-' : e.value)}
                            />
                            <Column
                                dataField='coN_Count'
                                caption='Const'
                                width={60}
                                allowResizing={false}
                                visible={!showDollars}
                                cellRender={(e) => (!e.value ? '-' : e.value)}
                            />
                            <Column
                                dataField='lnD_Count'
                                caption='Land'
                                width={60}
                                allowResizing={false}
                                visible={!showDollars}
                                cellRender={(e) => (!e.value ? '-' : e.value)}
                            />
                            <Column
                                dataField='rnT_Count'
                                caption='Rent'
                                width={60}
                                allowResizing={false}
                                visible={!showDollars}
                                cellRender={(e) => (!e.value ? '-' : e.value)}
                            />
                            <Column
                                dataField='b2R_Count'
                                caption='B2R'
                                width={60}
                                allowResizing={false}
                                visible={!showDollars}
                                cellRender={(e) => (!e.value ? '-' : e.value)}
                            />
                            <Column
                                dataField='fxF_Count'
                                caption='FXF'
                                width={60}
                                allowResizing={false}
                                visible={!showDollars}
                                cellRender={(e) => (!e.value ? '-' : e.value)}
                            />
                            <Column
                                dataField='total_Count'
                                caption='Total'
                                width={60}
                                allowResizing={false}
                                visible={!showDollars}
                                cellRender={(e) => (!e.value ? '-' : e.value)}
                            />
                            <Column
                                dataField='loC_Dollar'
                                caption='LOC'
                                width={60}
                                cellRender={(e) => (!e.value ? '-' : formatCurrency(e.value, 0))}
                                allowResizing={false}
                                visible={showDollars}
                            />
                            <Column
                                dataField='coN_Dollar'
                                caption='Const'
                                width={60}
                                cellRender={(e) => (!e.value ? '-' : formatCurrency(e.value, 0))}
                                allowResizing={false}
                                visible={showDollars}
                            />
                            <Column
                                dataField='lnD_Dollar'
                                caption='Land'
                                width={60}
                                cellRender={(e) => (!e.value ? '-' : formatCurrency(e.value, 0))}
                                allowResizing={false}
                                visible={showDollars}
                            />
                            <Column
                                dataField='rnT_Dollar'
                                caption='Rent'
                                width={60}
                                cellRender={(e) => (!e.value ? '-' : formatCurrency(e.value, 0))}
                                allowResizing={false}
                                visible={showDollars}
                            />
                            <Column
                                dataField='b2R_Dollar'
                                caption='B2R'
                                width={80}
                                cellRender={(e) => (!e.value ? '-' : formatCurrency(e.value, 0))}
                                allowResizing={false}
                                visible={showDollars}
                            />
                            <Column
                                dataField='fxF_Dollar'
                                caption='FXF'
                                width={60}
                                cellRender={(e) => (!e.value ? '-' : formatCurrency(e.value, 0))}
                                allowResizing={false}
                                visible={showDollars}
                            />
                            <Column
                                dataField='total_Dollar'
                                caption='Total'
                                width={60}
                                cellRender={(e) => (!e.value ? '-' : formatCurrency(e.value, 0))}
                                allowResizing={false}
                                visible={showDollars}
                            />
                            <Summary calculateCustomSummary={null}>
                                <TotalItem
                                    column='loC_Count'
                                    summaryType='sum'
                                    displayFormat='{0}'
                                    visible={!showDollars}
                                />
                                <TotalItem
                                    column='coN_Count'
                                    summaryType='sum'
                                    displayFormat='{0}'
                                    visible={!showDollars}
                                />
                                <TotalItem
                                    column='lnD_Count'
                                    summaryType='sum'
                                    displayFormat='{0}'
                                    visible={!showDollars}
                                />
                                <TotalItem
                                    column='rnT_Count'
                                    summaryType='sum'
                                    displayFormat='{0}'
                                    visible={!showDollars}
                                />
                                <TotalItem
                                    column='b2R_Count'
                                    summaryType='sum'
                                    displayFormat='{0}'
                                    visible={!showDollars}
                                />
                                <TotalItem
                                    column='fxF_Count'
                                    summaryType='sum'
                                    displayFormat='{0}'
                                    visible={!showDollars}
                                />
                                <TotalItem
                                    column='total_Count'
                                    summaryType='sum'
                                    displayFormat='{0}'
                                    visible={!showDollars}
                                />
                                <TotalItem
                                    column='loC_Dollar'
                                    summaryType='sum'
                                    displayFormat='{0}'
                                    visible={showDollars}
                                />
                                <TotalItem
                                    column='coN_Dollar'
                                    summaryType='sum'
                                    displayFormat='${0}'
                                    visible={showDollars}
                                />
                                <TotalItem
                                    column='lnD_Dollar'
                                    summaryType='sum'
                                    displayFormat='${0}'
                                    visible={showDollars}
                                />
                                <TotalItem
                                    column='rnT_Dollar'
                                    summaryType='sum'
                                    displayFormat='${0}'
                                    visible={showDollars}
                                />
                                <TotalItem
                                    column='b2R_Dollar'
                                    summaryType='sum'
                                    displayFormat='${0}'
                                    visible={showDollars}
                                />
                                <TotalItem
                                    column='fxF_Dollar'
                                    summaryType='sum'
                                    displayFormat='${0}'
                                    visible={showDollars}
                                />
                                <TotalItem
                                    column='total_Dollar'
                                    summaryType='sum'
                                    displayFormat='${0}'
                                    visible={showDollars}
                                />
                            </Summary>
                            <Paging
                                defaultPageSize={defaultPageSize}
                                enabled={defaultPageSize > 0}
                            />
                            <Pager
                                showPageSizeSelector={allowedPageSizes.length > 1}
                                allowedPageSizes={allowedPageSizes}
                                showNavigationButtons={true}
                                showInfo={true}
                                visible={true}
                            />
                        </DataGrid>
                    </div>
                </div>
                {e.data.nonEntityLinkedPrincipals && e.data.nonEntityLinkedPrincipals.length >= 1 && (
                    <>
                        <hr style={{ margin: '15px' }} />
                        <div style={styles.subordinateLinkageContainer}>
                            <div style={styles.subordinateLinkageGridContainer}>
                                <DataGrid
                                    id={'businessPrincipalsGrid'}
                                    keyExpr='entityId'
                                    dataSource={e.data.nonEntityLinkedPrincipals}
                                    applicationInsights={applicationInsights}
                                    pagerVisible={e.data.nonEntityLinkedPrincipals.length > 25}
                                    loadPanelEnabled={false}
                                    showBorders={true}
                                    showRowLines={true}
                                    rowAlternationEnabled={true}
                                    allowColumnResizing={true}
                                    selectionMode='none'
                                    remoteOperations={false}
                                    allowedPageSizes={[5, 10, 15]}
                                    defaultPageSize={5}
                                    repaintChangesOnly={true}
                                    noDataText='No Principals Found'
                                    width={'100%'}
                                >
                                    <Column
                                        datafield='highlight'
                                        cellRender={onHighlightCellRender}
                                        width='20'
                                    />
                                    <Column
                                        dataField='name'
                                        caption='Principal - Not Associated With Any Borrower Entity'
                                        cellRender={onBusinessLinkedPrincipalRender}
                                    />
                                    <Column
                                        type='actions'
                                        alignment='center'
                                        cellRender={(e) => onActionsCellRender(e, 'businessPrincipalsGrid')}
                                        width={50}
                                        allowReordering={false}
                                        allowSorting={false}
                                        allowResizing={false}
                                    />
                                    <Paging
                                        defaultPageSize={defaultPageSize}
                                        enabled={defaultPageSize > 0}
                                    />
                                    <Pager
                                        showPageSizeSelector={allowedPageSizes.length > 1}
                                        allowedPageSizes={allowedPageSizes}
                                        showNavigationButtons={true}
                                        showInfo={true}
                                        visible={true}
                                    />
                                </DataGrid>
                            </div>
                        </div>
                    </>
                )}
            </>
        );
    };

    if (!gridStore) {
        return '';
    }

    return (
        <>
            <DataGrid
                id={'borrowerGrid'}
                dataSource={dataStore}
                applicationInsights={applicationInsights}
                remoteOperations={true}
                wordWrapEnabled={true}
                rowAlternationEnabled={true}
                selectionFilter={'none'}
                ref={(ref) => setRef(ref)}
                allowColumnResizing={true}
                repaintChangesOnly={true}
            >
                <Column
                    dataField='name'
                    caption='Business Name'
                    width={200}
                    cellRender={onBusinessNameRender}
                    sortIndex={0}
                    sortOrder='asc'
                    className='dx_vertical_align'
                />
                <Column
                    dataField='city'
                    caption='City'
                    width={110}
                />
                <Column
                    dataField='state'
                    caption='State'
                    width={60}
                />
                <Column
                    type='actions'
                    alignment='center'
                    cellRender={(e) => onActionsCellRender(e, 'borrowerGrid')}
                    width={50}
                    allowReordering={false}
                    allowSorting={false}
                    allowResizing={false}
                />
                <Column
                    cellRender={onSubordinateLinkageRender}
                    allowSorting={false}
                />
                <Paging
                    defaultPageSize={defaultPageSize}
                    enabled={defaultPageSize > 0}
                />
                <Pager
                    showPageSizeSelector={allowedPageSizes.length > 1}
                    allowedPageSizes={allowedPageSizes}
                    showNavigationButtons={true}
                    showInfo={true}
                    visible={true}
                />
            </DataGrid>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
        </>
    );
};

export default BorrowersTable;
